/**
 * Web-app Huddle01 facade.
 *
 * Production transport + HTTP token provider now live in
 * `@slumbr/calling-web`. This file keeps the dev-only mock layer (mock
 * room ids + mock tokens) plus a couple of legacy helpers that the
 * messenger code imports today. Once the messenger store consumes the
 * `CallTransport` interface directly, this file can collapse to just
 * `isMockRoom` (and even that is debatable).
 */

import { getHuddleClient as getSharedClient, webCallTokenProvider } from "@slumbr/calling-web";

// ─── Environment ─────────────────────────────────────────────────────────────

const PROJECT_ID = import.meta.env.VITE_HUDDLE01_PROJECT_ID ?? "pi_ekQgao9FD56grfVz";
const DEV_AUTH_BYPASS = import.meta.env.VITE_DEV_AUTH_BYPASS === "true";

if (!PROJECT_ID) {
  console.warn("[Huddle01] Missing VITE_HUDDLE01_PROJECT_ID — calls will not work");
}

// ─── Client ─────────────────────────────────────────────────────────────────

/**
 * Re-exports the calling-web singleton — keeps `HuddleProvider` mount in
 * shell-layout.tsx working without an import-path change.
 */
export function getHuddleClient() {
  return getSharedClient(PROJECT_ID);
}

// ─── Room Creation ───────────────────────────────────────────────────────────

export interface CreateRoomResponse {
  roomId: string;
}

export async function createHuddleRoom(title: string): Promise<string> {
  if (DEV_AUTH_BYPASS) {
    // Mock room — no API call needed in dev mode.
    // The call overlay will detect the mock prefix and skip joinRoom().
    const mockRoomId = `mock-room-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    console.info(`[Huddle01 DEV] Created mock room: ${mockRoomId} (${title})`);
    return mockRoomId;
  }
  // Legacy callers only have `title`; conversationId is optional.
  return webCallTokenProvider.createRoom({ title });
}

// ─── Access Token Generation ─────────────────────────────────────────────────

export async function generateAccessToken(
  roomId: string,
  displayName: string
): Promise<string> {
  if (DEV_AUTH_BYPASS) {
    // Mock token — call overlay short-circuits joinRoom() for mock rooms,
    // so this token never reaches Huddle01.
    const mockToken = `mock-token-${roomId}-${displayName}`;
    console.info(`[Huddle01 DEV] Generated mock token for room ${roomId}`);
    return mockToken;
  }
  return webCallTokenProvider.mintAccessToken({
    roomId,
    identity: { userId: displayName, displayName },
  });
}

export async function sendCallInvite(input: {
  conversationId: string;
  roomId: string;
  callType: "voice" | "video";
  callerName: string;
}): Promise<void> {
  if (DEV_AUTH_BYPASS) return;
  await webCallTokenProvider.sendInvite(input);
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Check if a room ID is a mock (dev-only) room */
export function isMockRoom(roomId: string | undefined): boolean {
  return !!roomId && roomId.startsWith("mock-room-");
}

// ─── Exports ─────────────────────────────────────────────────────────────────

export { PROJECT_ID };
